import { React } from 'Imports';
import Button from '@mui/material/Button/Button';
import { isEncompass } from '$Utilities/integrationPlatform';
import { AutoScoreDrawer } from './AutoScoreDrawer';
import { AutoScoreDialog } from './AutoScoreDialog';
import { VideoEventService } from '$State/VideoEventFreezerService';
import { useEffect, useState } from 'react';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { FleetSettingsService } from '$State/FleetSettingsFreezerService';
import { AutoScoreSettingsResponse } from '$Generated/api';
import Grid from '@mui/material/Grid/Grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import IconButton from '@mui/material/IconButton/IconButton';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import { Alert } from 'MaterialUIComponents';

const styles = require('./AutoScoreEvents.scss') as {
    main: string;
    descriptionContainer: string;
    heading: string;
    description: string;
    assignButtonEncompass: string;
    assignButtonGeotab: string;
    tileEncompass: string;
    tileGeotab: string;
    autoScoreText: string;
    autoScoreActions: string;
    actionButton: string;
    deleteDialogContentGeotab: string;
    deleteDialogContentEncompass: string;
};

export const AutoScoreEvents = () => {
    const { showSnackbarMessage } = useAppSnackbar();

    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const [isEditEventAutoScore, setIsEditEventAutoScore] = useState(false);
    const [videoEventTypes, setVideoEventTypes] = useState<IDropdownItem[]>([]);
    const [videoEventRatingsState, setVideoEventRatingsState] = useState<IDropdownItem[]>([]);
    const [currentAutoScoreSettings, setCurrentAutoScoreSettings] = useState<AutoScoreSettingsResponse[]>([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [autoScoreSettingSelected, setAutoScoreSettingSelected] = useState<AutoScoreSettingsResponse | null>(null);

    const deleteDialogTitle = isEncompass ? 'Remove Auto-Score' : 'Confirm Auto-Score Removal';
    const deleteDialogCancelButton = isEncompass ? 'No, Go Back' : 'Cancel';
    const deleteDialogAcceptButton = isEncompass ? 'Yes, Remove' : 'Confirm';

    // Promoted video recall and Tamper Detect event types won't be included in the list.
    // Exclude the event types that are already assigned an auto-score.
    const getFilteredEventTypes = (excludedEventTypeIds: (number | undefined)[]) => {
        const { videoEventTypeResults } = VideoEventService.getState();
        return (
            videoEventTypeResults.data
                ?.filter((et) => et.id && !excludedEventTypeIds.includes(et.id) && et.id !== 7 && et.id !== 51)
                .map((eventType: any) => ({ label: eventType.displayName, value: eventType.id })) || []
        );
    };

    const fetchData = async () => {
        await FleetSettingsService.getAutoScoreSettingsByFleetResults();
        await VideoEventService.getVideoEventTypes();
        await VideoEventService.getVideoEventRatings();

        const { getAutoScoreSettingsByFleetResults } = FleetSettingsService.getState();
        const { videoEventRatings } = VideoEventService.getState();

        if (getAutoScoreSettingsByFleetResults) {
            setCurrentAutoScoreSettings(getAutoScoreSettingsByFleetResults.data || []);
        }

        if (videoEventRatings) {
            //format the score labels from database to match Figma design
            setVideoEventRatingsState(
                videoEventRatings.data?.map((score: any) => ({
                    label: formatScoreDisplayName(score.displayName),
                    value: score.id,
                })) || [],
            );
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatScoreDisplayName = (displayName: string) => {
        return displayName
            .replace('Positive ', '+')
            ?.replace('Critical/Collision', 'Critical')
            .replace(/^(-?\d+)\s+(.+)$/, '$1 ($2)');
    };

    const handleAssignEventAutoScoreClick = () => {
        const { videoEventTypeResults } = VideoEventService.getState();
        const { getAutoScoreSettingsByFleetResults } = FleetSettingsService.getState();

        if (videoEventTypeResults) {
            const currentEventTypeIdsSettingsByFleet =
                getAutoScoreSettingsByFleetResults.data?.map((setting: AutoScoreSettingsResponse) => setting.videoEventTypeId) || [];

            setVideoEventTypes(getFilteredEventTypes(currentEventTypeIdsSettingsByFleet));
        }

        setAutoScoreSettingSelected(null);
        setIsOpenDialog(true);
        setIsEditEventAutoScore(false);
    };

    const handleSaveAutoScoreClick = async (eventTypeId: number, scoreId: number) => {
        const action = isEditEventAutoScore ? 'saved' : 'assigned';

        await FleetSettingsService.saveAutoScoreSettings(
            isEditEventAutoScore ? autoScoreSettingSelected?.autoScoreSettingsId : undefined,
            eventTypeId,
            scoreId,
        )
            .then(() => {
                showSnackbarMessage({
                    message: `Auto-score ${action} successfully.`,
                    alertType: AlertTypes.Success,
                    isEncompass: isEncompass,
                    showIcon: isEncompass,
                    useNewIcons: true,
                });
                setIsOpenDialog(false);
                setIsEditEventAutoScore(false);
                fetchData();
            })
            .catch(() => {
                showSnackbarMessage({
                    message: `Auto-score unable to be ${action}.`,
                    alertType: AlertTypes.Fail,
                    isEncompass: isEncompass,
                    showIcon: isEncompass,
                    useNewIcons: true,
                });
            });
    };

    const handleRemoveAutoScoreClick = () => {
        const eventTypeId = autoScoreSettingSelected?.videoEventTypeId;
        if (!eventTypeId) return;
        FleetSettingsService.removeAutoScoreSetting(eventTypeId)
            .then(() => {
                showSnackbarMessage({
                    message: `Auto-score removed successfully.`,
                    alertType: AlertTypes.Success,
                    isEncompass: isEncompass,
                    showIcon: isEncompass,
                    useNewIcons: true,
                });
                fetchData();
            })
            .catch(() => {
                showSnackbarMessage({
                    message: 'Auto-score unable to be removed.',
                    alertType: AlertTypes.Fail,
                    isEncompass: isEncompass,
                    showIcon: isEncompass,
                    useNewIcons: true,
                });
            })
            .finally(() => {
                handleCloseDeleteDialog();
            });
    };

    const handleEditAutoScoreClick = (autoScoreSetting: AutoScoreSettingsResponse) => {
        // Get event type IDs that should be excluded (all current settings except the one being edited)
        const excludedEventTypeIds = currentAutoScoreSettings
            .filter((setting) => setting.videoEventTypeId !== autoScoreSetting.videoEventTypeId)
            .map((setting) => setting.videoEventTypeId);

        setVideoEventTypes(getFilteredEventTypes(excludedEventTypeIds));
        setAutoScoreSettingSelected(autoScoreSetting);
        setIsEditEventAutoScore(true);
        setIsOpenDialog(true);
    };

    const handleDeleteAutoScoreClick = (autoScoreSetting: AutoScoreSettingsResponse) => {
        setAutoScoreSettingSelected(autoScoreSetting);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setAutoScoreSettingSelected(null);
    };

    return (
        <>
            <div className={styles.main}>
                <div className={styles.descriptionContainer}>
                    <div>
                        <div className={styles.heading}>Auto-Score Events</div>
                        <div className={styles.description}>
                            Automatically assign a score to an event type to save time in your video review process.
                        </div>
                    </div>

                    <Button
                        className={isEncompass ? styles.assignButtonEncompass : styles.assignButtonGeotab}
                        onClick={handleAssignEventAutoScoreClick}
                    >
                        Assign
                    </Button>
                </div>
                <Grid container spacing={2}>
                    {currentAutoScoreSettings.map((autoScoreSetting, index) => (
                        <Grid item xs={12} sm={12} md={12} lg={6} xl={4} key={index}>
                            <div className={isEncompass ? styles.tileEncompass : styles.tileGeotab}>
                                <div className={styles.autoScoreText}>
                                    <b>{autoScoreSetting.videoEventTypeDisplayName}</b>
                                    <br />
                                    <span>{'Auto-score ' + formatScoreDisplayName(autoScoreSetting.videoEventScoreDisplayName ?? '')}</span>
                                </div>
                                <div className={styles.autoScoreActions}>
                                    <IconButton
                                        className={styles.actionButton}
                                        title={'Edit'}
                                        onClick={() => handleEditAutoScoreClick(autoScoreSetting)}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </IconButton>

                                    <IconButton
                                        className={styles.actionButton}
                                        title={'Delete'}
                                        onClick={() => handleDeleteAutoScoreClick(autoScoreSetting)}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </IconButton>
                                </div>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            </div>

            {isEncompass ? (
                <AutoScoreDrawer
                    open={isOpenDialog}
                    onClose={() => setIsOpenDialog(false)}
                    isEdit={isEditEventAutoScore}
                    handleAssignEventAutoScore={handleSaveAutoScoreClick}
                    eventTypesItems={videoEventTypes}
                    scoresItems={videoEventRatingsState}
                    selectedAutoScoreSetting={autoScoreSettingSelected}
                />
            ) : (
                <AutoScoreDialog
                    open={isOpenDialog}
                    onClose={() => setIsOpenDialog(false)}
                    isEdit={isEditEventAutoScore}
                    handleAssignEventAutoScore={handleSaveAutoScoreClick}
                    eventTypesItems={videoEventTypes}
                    scoresItems={videoEventRatingsState}
                    selectedAutoScoreSetting={autoScoreSettingSelected}
                />
            )}

            <ConfirmDialog
                isOpen={isDeleteDialogOpen}
                title={deleteDialogTitle}
                maxWidth={isEncompass ? '488px' : '526px'}
                contentMessageMarginTop={isEncompass ? '0' : undefined}
                contentMessage={
                    isEncompass ? (
                        <div>
                            <div>Are you sure you want to remove auto-scoring from this event type?</div>
                            <div className={styles.deleteDialogContentEncompass}>
                                <strong>
                                    {autoScoreSettingSelected?.videoEventTypeDisplayName} events going forward will no longer be
                                    auto-scored.
                                </strong>
                            </div>
                        </div>
                    ) : (
                        <>
                            <span>Are you sure you want to remove auto-scoring from this event type?</span>
                            <Alert color="error" icon={false} className={styles.deleteDialogContentGeotab}>
                                <strong>{autoScoreSettingSelected?.videoEventTypeDisplayName}</strong> events going forward will no longer
                                be auto-scored.
                            </Alert>
                        </>
                    )
                }
                isAlert={isEncompass}
                cancelButton={deleteDialogCancelButton}
                acceptButton={deleteDialogAcceptButton}
                isButtonAlert={isEncompass}
                handleModalClosed={handleCloseDeleteDialog}
                handleSubmit={handleRemoveAutoScoreClick}
            />
        </>
    );
};
